import { useMemo } from "react";
import useSWR from "swr";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { CatalogEntityType, mapAggregatedType } from "../types";

export interface EnrichedLO {
  entity: Record<string, any>;
  entityType: CatalogEntityType;
}

interface UseLOEntitiesByIdsParams {
  corporateId: string;
  ids: number[];
  enabled?: boolean;
}

const normalizeIds = (ids: number[]) =>
  Array.from(new Set(ids)).sort((a, b) => a - b);

const buildUrl = (corporateId: string) =>
  ServicesURL.getAggregatedLO.replace(":idCorporate", corporateId);

const buildBody = (sortedIds: number[]) => ({
  conditions: [{ field: "id", operation: "in", value: sortedIds }],
  orderBy: [{ field: "id", direction: "desc" }],
  page: 0,
  size: sortedIds.length,
});

const mapEntitiesById = (data: any): Map<number, EnrichedLO> => {
  const map = new Map<number, EnrichedLO>();
  const items: any[] = data?.output ?? [];
  items.forEach((raw: any) => {
    const lo = raw?.lo;
    const id = Number(lo?.id);
    if (!Number.isFinite(id)) return;
    map.set(id, { entity: lo, entityType: mapAggregatedType(raw?.type) });
  });
  return map;
};

export const useLOEntitiesByIds = ({
  corporateId,
  ids,
  enabled = true,
}: UseLOEntitiesByIdsParams) => {
  const sortedIds = useMemo(() => normalizeIds(ids), [ids]);

  const url =
    enabled && corporateId && sortedIds.length > 0
      ? buildUrl(corporateId)
      : null;

  const body = useMemo(() => buildBody(sortedIds), [sortedIds]);

  const { data, isLoading, error } = useSWR(
    url ? [url, body] : null,
    ([u, b]: [string, typeof body]) => fetcher(u, MethodType.POST, b),
  );

  const entitiesById = useMemo(() => mapEntitiesById(data), [data]);

  return { entitiesById, isLoading, error };
};
